
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";

// Live progress for "Find rør": the centre-line growing forward and backward from the start, each with a marker at the
// point the search has reached, and a marker at the start. Shown only WHILE the calculation runs and disposed when it
// ends (the result itself becomes two ordinary registrations). Not authoritative geometry and never stored.
//
// The geometry is built LOCAL to the start and the large world offset sits on the object's position (float64):
// UTM-scale coordinates in a Float32 vertex buffer lose centimetres and flicker under camera movement.

// high contrast on blue/orange/green pipes and soil alike; each line runs over a dark, wider underlay
const TRACK_COLORS = {forward: 0xffe600, backward: 0xff2bd6};
const UNDERLAY_COLOR = 0x000000;
const UNDERLAY_WIDTH_PX = 8;
const START_COLOR = 0xffffff;
const LINE_WIDTH_PX = 4;
const TIP_SIZE_PX = 16;
const START_SIZE_PX = 12;

let discTexture = null;
function getDiscTexture(){
	if(discTexture) return discTexture;
	const c = document.createElement("canvas");
	c.width = c.height = 64;
	const g = c.getContext("2d");
	g.beginPath(); g.arc(32, 32, 28, 0, Math.PI * 2);
	g.fillStyle = "#ffffff"; g.fill();
	g.lineWidth = 6; g.strokeStyle = "rgba(0,0,0,0.75)"; g.stroke();
	discTexture = new THREE.CanvasTexture(c);
	return discTexture;
}

function makeMarker(color, sizePx){
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0], 3));
	const material = new THREE.PointsMaterial({color, size: sizePx, sizeAttenuation: false, map: getDiscTexture(),
		transparent: true, alphaTest: 0.5, depthTest: false, depthWrite: false});
	const marker = new THREE.Points(geometry, material);
	marker.frustumCulled = false;
	marker.renderOrder = 1001;
	marker.visible = false;
	return marker;
}

export class PipeTraceVisual extends THREE.Object3D {
	constructor(){
		super();
		this.name = "PipeTraceLivePreview";
		this._origin = null;
		this._tracks = {};
		const makeLine = (color, widthPx, opacity, order) => {
			const g = new LineGeometry();
			g.setPositions([0, 0, 0, 0, 0, 0]);
			const m = new LineMaterial({color, linewidth: widthPx, transparent: true, opacity, resolution: new THREE.Vector2(1000, 1000)});
			m.depthTest = false;
			const line = new Line2(g, m);
			line.visible = false;
			line.renderOrder = order;
			return line;
		};
		for(const direction of Object.keys(TRACK_COLORS)){
			const under = makeLine(UNDERLAY_COLOR, UNDERLAY_WIDTH_PX, 0.55, 999);
			const line = makeLine(TRACK_COLORS[direction], LINE_WIDTH_PX, 1, 1000);
			const tip = makeMarker(TRACK_COLORS[direction], TIP_SIZE_PX);
			this.add(under, line, tip);
			this._tracks[direction] = {lines: [under, line], tip};
		}
		this._start = makeMarker(START_COLOR, START_SIZE_PX);
		this.add(this._start);
		this._onUpdate = () => this._syncResolution();
		this._boundViewer = null;
	}

	// call once, right after adding to the scene
	attach(viewer){
		this._boundViewer = viewer;
		viewer.addEventListener("update", this._onUpdate);
	}

	_syncResolution(){
		if(!this._boundViewer) return;
		const size = this._boundViewer.renderer.getSize(new THREE.Vector2());
		for(const {lines} of Object.values(this._tracks)) for(const line of lines) line.material.resolution.set(size.width, size.height);
	}

	_local(p, target){
		return target.set(p.x - this._origin.x, p.y - this._origin.y, p.z - this._origin.z);
	}

	// one direction's growing centre-line (`centers`, starting at the start point) and where its search stands (`tip`)
	updateTrack(direction, centers, tip){
		const track = this._tracks[direction];
		if(!track || !centers || centers.length < 1) return;
		if(!this._origin){
			this._origin = new THREE.Vector3(centers[0].x, centers[0].y, centers[0].z);
			this.position.copy(this._origin);
			this._start.visible = true; // the start sits at the local origin
		}
		const v = new THREE.Vector3();
		if(centers.length >= 2){
			const flat = new Array(centers.length * 3);
			for(let i = 0; i < centers.length; i++){
				this._local(centers[i], v);
				flat[i * 3] = v.x; flat[i * 3 + 1] = v.y; flat[i * 3 + 2] = v.z;
			}
			for(const line of track.lines){
				line.geometry.setPositions(flat);
				line.geometry.computeBoundingSphere();
				line.visible = true;
			}
		}
		const head = tip || centers[centers.length - 1];
		this._local(head, v);
		const attr = track.tip.geometry.getAttribute("position");
		attr.setXYZ(0, v.x, v.y, v.z);
		attr.needsUpdate = true;
		track.tip.visible = true;
		this._syncResolution();
	}

	dispose(){
		if(this._boundViewer) this._boundViewer.removeEventListener("update", this._onUpdate);
		this._boundViewer = null;
		for(const {lines, tip} of Object.values(this._tracks)){
			for(const line of lines){ line.geometry.dispose(); line.material.dispose(); }
			tip.geometry.dispose(); tip.material.dispose();
		}
		this._start.geometry.dispose(); this._start.material.dispose();
		if(this.parent) this.parent.remove(this);
	}
}
